import React, { useEffect, useState } from 'react'
import TimeAgo from 'timeago-react'
import LineChart from 'component/chart/line/line-chart'
import Api from 'utils/api'
import KPISimple from 'component/KPIs/kpi-simple'
import KPILead from 'component/KPIs/kpi-lead'
import CampaignList from './campaign-list'
import MerchantTeam from './team'
import MerchantOverview from './overview'
import MerchantBranding from './branding'
import { CurrencyFormat } from 'utils'
import { useLocation, useNavigate } from 'react-router-dom'

const TABS = ['Stats', 'Overview', 'Team', 'Branding']

function ViewDashboard() {
  const { state: location } = useLocation()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('Stats')
  const [monthlySale, setMonthlySale] = useState({})
  const [recentSale, setRecentSale] = useState()
  const [locationKPIs, setLocationKPIs] = useState({})

  const getMonthlySale = async () => {
    const { response } = await Api('/reports/monthly_sale?location_id=' + location._id, 'get')
    if (response) {
      setMonthlySale({ labels: response.data?.labels, datasets: { label: 'Month sales', data: response.data?.data } })
      setRecentSale(response.data.recent_sale)
    }
  }

  const getLocationKPIs = async () => {
    const { response } = await Api('/reports/location/kpis?location_id=' + location._id, 'get')
    if (response) setLocationKPIs(response.data)
  }

  useEffect(() => {
    getMonthlySale()
    getLocationKPIs()
  }, [])

  const getSaleStatus = () => {
    const d = monthlySale.datasets?.data || []
    if (d.length < 2) return '—'
    const pct = (((d[d.length - 1] - d[d.length - 2]) / (d[d.length - 2] || 1)) * 100).toFixed()
    return (pct > 0 ? '+' : '') + pct + '%'
  }

  const currency = location?.basic_info?.currency_code

  if (!location) {
    return (
      <div className='p-8 text-center'>
        <p className='text-gray-500'>No merchant selected.</p>
        <button onClick={() => navigate('/accounts/listing')} className='mt-4 text-blue-600 hover:underline text-sm'>
          Back to Merchants
        </button>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className='flex items-center justify-between mb-4'>
        <div>
          <button onClick={() => navigate('/accounts/listing')} className='text-sm text-blue-500 hover:underline mb-1'>
            ← Merchants
          </button>
          <h1 className='text-2xl font-bold text-gray-800'>{location.name || 'Merchant'}</h1>
          <p className='text-sm text-gray-400'>{location.user?.email}</p>
        </div>
        <span className={`text-sm px-3 py-1 rounded-full font-medium ${
          location.active === false ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'
        }`}>
          {location.active === false ? 'Suspended' : 'Active'}
        </span>
      </div>

      {/* Tabs */}
      <div className='flex gap-1 border-b border-gray-200 mb-6'>
        {TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === tab
                ? 'bg-white border border-b-white border-gray-200 text-[#304861] -mb-px'
                : 'text-gray-500 hover:text-[#304861]'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Stats Tab */}
      {activeTab === 'Stats' && (
        <div>
          <div className='grid gap-6 mb-6 lg:grid-cols-4'>
            <KPILead
              label={locationKPIs.top_campaign?.name}
              value={CurrencyFormat({ amount: locationKPIs.top_campaign_amount, currency })}
              description='Top Campaign'
            />
            <KPILead
              label='Active Subscribers'
              value={locationKPIs.active_subscribers}
              description={<><span className='text-green-500 font-semibold'>{locationKPIs.total_subscriber}</span> &nbsp;Total</>}
            />
            <KPILead
              label="Today's Revenue"
              value={CurrencyFormat({ amount: locationKPIs.total_amount_today, currency })}
              description={<><span className='text-green-500 font-semibold'>{getSaleStatus()}</span> &nbsp;vs yesterday</>}
            />
            <KPILead
              label={locationKPIs.top_customer?.fullname}
              value={CurrencyFormat({ amount: locationKPIs.top_customer_spent, currency })}
              description='Top Customer'
            />
          </div>
          <div className='lg:grid lg:grid-cols-12 gap-6'>
            <div className='col-span-8 grid lg:grid-cols-3 gap-4'>
              <KPISimple label='Total Campaigns' value={locationKPIs.total_campaign} />
              <KPISimple label='Total Passes' value={locationKPIs.total_pass} />
              <KPISimple label='Total Amount' value={CurrencyFormat({ amount: locationKPIs.total_amount, currency })} />
              <KPISimple label='Total Visits' value={locationKPIs.total_visit} />
              <KPISimple label='Passes in Wallet' value={locationKPIs.total_pass_wallet} />
              <KPISimple label='Passes Redeemed' value={locationKPIs.total_pass_redeem} />
            </div>
            <div className='col-span-4 mt-6 lg:mt-0'>
              <LineChart
                chart={monthlySale}
                description={<>(<strong>{getSaleStatus()}</strong>) vs last month</>}
                date={<>Last sale &nbsp;<TimeAgo datetime={recentSale?.createdAt} live={false} /></>}
              />
            </div>
          </div>
          <div className='mt-6'>
            <CampaignList location={location} />
          </div>
        </div>
      )}

      {/* Overview Tab */}
      {activeTab === 'Overview' && <MerchantOverview location={location} />}

      {/* Team Tab */}
      {activeTab === 'Team' && <MerchantTeam location={location} />}

      {/* Branding Tab */}
      {activeTab === 'Branding' && <MerchantBranding location={location} />}
    </div>
  )
}

export default ViewDashboard
